'use client';

// Cache "hiện ngay rồi làm mới ngầm" (stale-while-revalidate) cho DỮ LIỆU TRANG.
//
// VÌ SAO: gần như toàn bộ dữ liệu GMS đi THẲNG browser → Firestore bằng `getDocs` (server-first),
// nên mỗi lần F5 / quay lại trang là tải lại từ đầu, kể cả vừa mở cách đó 10 giây. Firestore
// `persistentLocalCache` KHÔNG giải quyết được: nó chỉ phục vụ `onSnapshot`, còn `getDocs` vẫn
// luôn hỏi server. Redis cũng không: nó nằm ở server mà các truy vấn này không hề đi qua server.
// ⇒ Chỗ đúng để cache là NGAY TRÊN MÁY người dùng. Xem docs/perf-boot.md.
//
// Lưu ở IndexedDB (không phải localStorage): payload trang có thể ~1 MB, localStorage chỉ ~5 MB
// tổng cho cả origin và ghi/đọc ĐỒNG BỘ (chặn luồng chính, đúng lúc đang cần mượt nhất).
//
// ⚠️ RANH GIỚI BẢO MẬT vẫn là Firestore Rules. Cache chỉ là bản sao dữ liệu user ĐÃ đọc được hợp lệ;
//    bị thu quyền thì lượt làm mới kế tiếp lỗi và ta xoá cache (xem `dropCache` khi permission-denied).

import { Timestamp } from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { logClientError } from './client-error-logger';
import { recordCacheResult } from './analytics';

const DB_NAME = 'gms-swr';
const STORE = 'pages';
const DB_VERSION = 1;
const DEFAULT_TTL_MS = 24 * 3600_000;   // quá hạn → coi như không có cache (không hiện dữ liệu quá cũ)

import { STAMP_MAX_AGE_MS } from './data-stamp';

interface Entry { k: string; json: string; at: number; stamp?: number | null }

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'k' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);      // chế độ riêng tư / storage bị chặn → chạy không cache
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGet(key: string): Promise<Entry | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as Entry) ?? null);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

async function idbPut(entry: Entry): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try { db.transaction(STORE, 'readwrite').objectStore(STORE).put(entry); } catch { /* quota/đóng → bỏ qua */ }
}

async function idbDel(key: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try { db.transaction(STORE, 'readwrite').objectStore(STORE).delete(key); } catch { /* bỏ qua */ }
}

/**
 * Firestore `Timestamp` mất PROTOTYPE khi đi qua JSON — chỗ nào gọi `.toDate()`/`.toMillis()` trên
 * dữ liệu lấy từ cache sẽ nổ. Nên phải dựng lại đúng lớp.
 *
 * ⚠️ BUG THẬT (đã tái phát 2 lần — 20/09 `bk.createdAt.toMillis`, 23/09 `e.toDate` ở autoPushHist
 * `/timeline` — cùng MỘT gốc rễ, lần đầu bị chẩn đoán sai thành "booking cũ/import qua script thiếu
 * method" rồi chỉ vá triệu chứng bằng optional chaining thay vì sửa hàm này): check cũ đòi ĐÚNG 2
 * khoá `{seconds, nanoseconds}`, dựa trên giả định SAI. `Timestamp.toJSON()` thật của SDK client
 * (`node_modules/@firebase/firestore`) trả **BA** khoá — `{type:'firestore/timestamp/1.0', seconds,
 * nanoseconds}` — nên `JSON.stringify()` một Timestamp THẬT (không phải object dựng tay) không bao
 * giờ khớp `keys.length===2`, revive luôn thất bại âm thầm, để lại plain object thiếu `.toDate()`.
 * Nhận diện qua SỰ HIỆN DIỆN `seconds`+`nanoseconds` dạng number, chấp nhận CẢ 2 khoá (object dựng
 * tay, không đi qua Timestamp.toJSON) LẪN 3 khoá có `type` đúng tag — không đếm tổng số khoá.
 * ⚠️ Thêm kiểu dữ liệu Firestore khác vào cache (GeoPoint, DocumentReference…) thì PHẢI xử lý ở đây.
 *
 * Export để `lib/boot-cache.ts` dùng CHUNG — đó là cache localStorage THỨ HAI trong app lưu dữ liệu
 * Firestore thô (buildings, có field `createdAt`/`history` kiểu Timestamp) qua JSON mà TRƯỚC ĐÂY
 * không hề gọi hàm này (0 lượt revive) — chưa crash vì hiện chưa có chỗ nào đọc 2 field đó từ
 * `useBuilding().buildings`, nhưng đúng bài học ở trên: đừng đợi lỗi tái phát lần 3 ở cache thứ ba.
 */
export function reviveTimestamps(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(reviveTimestamps);
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const keys = Object.keys(o);
    const looksLikeTimestamp = typeof o.seconds === 'number' && typeof o.nanoseconds === 'number'
      && (keys.length === 2 || (keys.length === 3 && o.type === 'firestore/timestamp/1.0'));
    if (looksLikeTimestamp) {
      return new Timestamp(o.seconds as number, o.nanoseconds as number);
    }
    const out: Record<string, unknown> = {};
    for (const k of keys) out[k] = reviveTimestamps(o[k]);
    return out;
  }
  return v;
}

/** Xoá 1 khoá cache (dùng khi lượt làm mới bị Firestore từ chối quyền). */
export function dropCache(key: string): void { void idbDel(key); }

/**
 * Đọc/ghi cache THÔ — KHÔNG có logic dấu mốc/tự tải lại của `useSwrData`. Dùng cho dữ liệu REALTIME
 * (`onSnapshot`) muốn vẽ ngay từ lần trước trong lúc listener thật đang kết nối, rồi để listener tự
 * ghi đè khi có dữ liệu mới — khác hẳn stale-while-revalidate một lượt của `useSwrData` (không hợp
 * với `onSnapshot`: không có "một lượt tải" nào để chờ). Cùng kho IndexedDB `gms-swr`, khác store key.
 *
 * ⚠️ `key` PHẢI tự chịu trách nhiệm về TÍNH HỢP LỆ theo thời điểm (vd nhét ngày vào key nếu dữ liệu
 * chỉ đúng trong ngày) — hàm này không tự biết cache có còn hợp lệ về mặt NGHIỆP VỤ hay không, chỉ
 * biết nó có quá hạn `maxAgeMs` hay chưa.
 */
export async function readRealtimeCache<T>(key: string, maxAgeMs = DEFAULT_TTL_MS): Promise<T | null> {
  const cached = await idbGet(key);
  if (!cached || Date.now() - cached.at > maxAgeMs) return null;
  try { return reviveTimestamps(JSON.parse(cached.json)) as T; } catch { return null; }
}

export function writeRealtimeCache<T>(key: string, data: T): void {
  void idbPut({ k: key, json: JSON.stringify(data), at: Date.now() });
}

interface SwrResult<T> {
  data: T | null;
  /** true = chưa có gì để vẽ (không cache, đang tải lần đầu). */
  loading: boolean;
  /** true = đang hiển thị dữ liệu CŨ từ cache, lượt làm mới chưa xong → nên hiện chip "Đang cập nhật…". */
  stale: boolean;
  refresh: () => void;
}

/**
 * @param key    khoá cache — PHẢI gồm tenantId + mọi tham số ảnh hưởng kết quả (toà, tháng…),
 *               nếu không tenant/toà này sẽ thấy dữ liệu cache của tenant/toà khác.
 *               Truyền `null` để tạm không chạy (vd đang chờ danh sách toà).
 * @param ttlMs  quá hạn thì bỏ qua cache, chờ dữ liệu thật (mặc định 24h).
 */
/**
 * ĐỌC DẤU MỐC — trả số "lần đổi gần nhất" của bảng nguồn, hoặc `null` nếu không đọc được.
 *
 * Có hàm này thì `useSwrData` BỎ QUA hẳn lượt tải khi dấu mốc trùng với bản đã lưu: đây là khác biệt
 * với stale-while-revalidate thường (luôn tải lại để biết có gì mới, nên không cắt được lượt đọc nào).
 * `null` ⇒ coi như không biết ⇒ tải lại cho chắc. Xem `lib/data-stamp.ts`.
 */
export type StampReader = () => Promise<number | null>;

export function useSwrData<T>(
  key: string | null,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS,
  stampReader?: StampReader,
): SwrResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick(t => t + 1), []);

  // Giữ fetcher mới nhất qua ref → đổi identity của fetcher (rất hay xảy ra vì nó là closure trong
  // component) KHÔNG kích hoạt fetch lại; chỉ `key` và `refresh()` mới điều khiển việc tải.
  const fetcherRef = useRef(fetcher);
  const stampRef = useRef(stampReader);
  // Ghi ref trong lúc render là không hợp lệ ở React 19 (render phải thuần; StrictMode chạy render
  // 2 lần là ref lệch). Ref chỉ được đọc trong effect/handler nên cập nhật sau render là đủ.
  useEffect(() => { fetcherRef.current = fetcher; stampRef.current = stampReader; });

  useEffect(() => {
    if (!key) { setLoading(false); return; }
    let cancelled = false;

    (async () => {
      const cached = await idbGet(key);
      if (cancelled) return;
      if (cached && Date.now() - cached.at <= ttlMs) {
        try {
          setData(reviveTimestamps(JSON.parse(cached.json)) as T);
          setLoading(false);
          setStale(true);
        } catch {
          void idbDel(key);   // JSON hỏng → bỏ, coi như chưa có cache
        }
      }

      // ── BỎ QUA LƯỢT TẢI khi nguồn không đổi ────────────────────────────────────────────────────
      // Chỉ dám bỏ khi CẢ BA điều kiện: có cache đọc được, dấu mốc trùng, và cache chưa quá hạn cứng.
      // Hạn cứng `STAMP_MAX_AGE_MS` là lưới an toàn: nếu có đường ghi nào quên đóng dấu thì thiệt hại
      // chặn ở 15 phút chứ không kéo dài mãi (cùng lý lẽ TTL sổ đăng ký tenant).
      let stamp: number | null = null;
      if (stampRef.current) {
        try {
          stamp = await stampRef.current();
        } catch (err) {
          // Đọc dấu mốc hỏng → `null` → tải lại như cũ (đúng, chỉ tốn lượt đọc). Nhưng PHẢI kêu:
          // hỏng kéo dài nghĩa là cơ chế cache coi như tắt, và nó tắt trong im lặng thì không ai biết
          // vì sao hoá đơn Firestore không giảm như mong đợi.
          stamp = null;
          logClientError({ route: 'lib/swr-cache.ts', action: 'read_data_stamp',
            message: err instanceof Error ? err.message : String(err), details: { key } });
        }
        if (cancelled) return;
        const hit = stamp !== null && !!cached && cached.stamp === stamp && Date.now() - cached.at <= STAMP_MAX_AGE_MS;
        // Ghi CẢ hit lẫn miss — chỉ đếm hit thì không biết mẫu số, mà không có mẫu số thì con số vô nghĩa.
        // Nhãn cắt phần biến thiên (tenantId) để gộp được: `bookings:gardenia:recent500` → `bookings`.
        recordCacheResult(key.split(':')[0], hit);
        if (hit) {
          setStale(false);
          setLoading(false);
          return;
        }
      }

      try {
        const fresh = await fetcherRef.current();
        if (cancelled) return;
        setData(fresh);
        setStale(false);
        void idbPut({ k: key, json: JSON.stringify(fresh), at: Date.now(), stamp });
      } catch (err) {
        if (cancelled) return;
        const code = (err as { code?: string })?.code;
        if (code === 'permission-denied') { void idbDel(key); setData(null); }
        setStale(false);   // đã cố làm mới và thất bại — đừng để chip "đang cập nhật" quay mãi
        logClientError({ route: 'lib/swr-cache.ts', action: 'revalidate', message: err instanceof Error ? err.message : String(err), details: { key } });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [key, ttlMs, tick]);

  return { data, loading, stale, refresh };
}
